// Piezas de Promociones en la venta cerrada (Mis ventas, comprobante y detalle del Admin VL).
import { useMemo } from 'react';
import { isPromotionsConfigured } from '@/domains/retail/admin/promotions/api/promotions.client';
import type { SaleSlots } from '@/shared/patterns/SaleReceipt';
import { ClosedSaleGiftName } from '../components/ClosedSaleGiftName';
import { LinePromotionTag } from '../components/LinePromotionTag';
import { ClosedSaleBenefits } from '../components/SaleClosingBenefits';
import type { SaleAudience } from '../lib/saleAudience';
import { useAuth } from '@/hooks/useAuth';
import { choosesRetailers } from '@/domains/retail/admin/promotions/lib/promotionsRetailerMode';

/**
 * `retailerId` deja al Admin VL nombrar regalos desde el catálogo de la cadena. Con
 * `couponByRetailer`, el admin de tenant también lee el cupón de esa cadena.
 */
export function buildPromotionSaleSlots(
    audience: SaleAudience,
    retailerId?: string,
    couponByRetailer = false,
): SaleSlots {
    const scope = retailerId ? { retailerId } : {};
    const coupon = couponByRetailer && retailerId ? { couponRetailerId: retailerId } : {};
    return {
        linePromotion: (line, sale) => (
            <LinePromotionTag line={line} saleId={sale.saleId} audience={audience} {...coupon} />
        ),
        giftName: (line, sale, lineName) => (
            <ClosedSaleGiftName
                saleId={sale.saleId}
                audience={audience}
                sku={line.sku}
                lineName={lineName}
                {...scope}
            />
        ),
        saleBenefits: (sale) => (
            <ClosedSaleBenefits sale={sale} audience={audience} {...scope} {...coupon} />
        ),
    };
}

/** Lugares de la venta cerrada para quien monta (`ADMIN` o `STORE`); vacío sin Promociones. */
export function usePromotionSaleSlots(audience: SaleAudience, retailerId?: string): SaleSlots {
    const configured = isPromotionsConfigured();
    const { roleAssignments } = useAuth();
    const couponByRetailer = audience === 'ADMIN' && choosesRetailers(roleAssignments);
    return useMemo(
        () => (configured ? buildPromotionSaleSlots(audience, retailerId, couponByRetailer) : {}),
        [configured, audience, retailerId, couponByRetailer],
    );
}
